/** 核价预算后端访问层，统一经 httpClient 出口并解信封。 */

import type { ApiEnvelope } from "../../../lib/http/contracts";
import { httpClient } from "../../../lib/http/axios";
import {
  mapCopyVersionPayload,
  mapCopyVersionResult,
  mapEditorData,
  mapEnterResult,
  mapFinalizePayload,
  mapFinalizeResult,
  mapHistoryBudgetSearchResult,
  mapComparisonOptions,
  mapVersionCompareResult,
  mapPriceCandidate,
  mapPriceReusePreview,
  mapRebuildVersionPayload,
  mapTransportPoint,
  mapSaveDraftPayload,
} from "../api/adapters";
import type {
  CopyVersionRequest,
  CopyVersionResult,
  EnterItineraryBudgetResult,
  FinalizeRequest,
  FinalizeResult,
  QuoteBudgetEditorData,
  QuoteBudgetHistorySearchResult,
  QuoteBudgetComparisonOptions,
  QuoteBudgetVersionCompareResult,
  QuoteBudgetPriceCandidate,
  PriceReusePreview,
  QuoteBudgetTransportPoint,
  RebuildVersionRequest,
  SaveDraftRequest,
} from "../types";

type Raw = Record<string, unknown>;

/** 只读定位核价已有报价；找不到时由调用方判断是否具备创建权限。 */
export async function findExistingCustomerQuote(budgetOrderId: string): Promise<string> {
  const response = await httpClient.get<ApiEnvelope<{ customer_quote_id: string }>>(
    `/customer-quotes/budgets/${encodeURIComponent(budgetOrderId)}/existing`,
  );
  return unwrap(response.data).customer_quote_id;
}

/** 解信封：非 OK 或空数据抛错，否则返回 data. */
function unwrap<T>(envelope: ApiEnvelope<T>): T {
  if (envelope.code !== "OK" || envelope.data === null || envelope.data === undefined) {
    throw new Error(`行程核价请求失败：${envelope.code}`);
  }
  return envelope.data;
}

/** 进入核价预算：从行程取得或创建预算单。 */
export async function enterItineraryBudget(
  itineraryId: string,
): Promise<EnterItineraryBudgetResult> {
  const response = await httpClient.post<ApiEnvelope<Raw>>(
    `/quote-budgets/itineraries/${itineraryId}/enter`,
    undefined,
    { timeout: 60_000, "axios-retry": { retries: 0 } },
  );
  return mapEnterResult(unwrap(response.data));
}

/** 查询编辑器数据。 */
export async function fetchBudgetEditor(budgetOrderId: string): Promise<QuoteBudgetEditorData> {
  const response = await httpClient.get<ApiEnvelope<Raw>>(`/quote-budgets/${budgetOrderId}/editor`);
  return mapEditorData(unwrap(response.data));
}

/** 手动按最新行程覆盖重生成 V1，不新建版本号。 */
export async function regenerateV1FromItinerary(
  budgetOrderId: string,
): Promise<QuoteBudgetEditorData> {
  const response = await httpClient.post<ApiEnvelope<Raw>>(
    `/quote-budgets/${budgetOrderId}/regenerate-v1`,
    undefined,
    { timeout: 60_000, "axios-retry": { retries: 0 } },
  );
  return mapEditorData(unwrap(response.data));
}

/** 按课程线路标题查询其他行程的全部已定稿核价。 */
export async function searchHistoryBudgets(
  targetBudgetOrderId: string,
  title: string,
): Promise<QuoteBudgetHistorySearchResult> {
  const response = await httpClient.get<ApiEnvelope<Raw>>(
    `/quote-budgets/${targetBudgetOrderId}/history`,
    { params: { title, limit: 20 } },
  );
  return mapHistoryBudgetSearchResult(unwrap(response.data));
}

/** 搜索版本比较页可选的全部行程版本（包含草稿与定稿）。 */
export async function searchComparisonOptions(
  title: string,
  signal?: AbortSignal,
): Promise<QuoteBudgetComparisonOptions> {
  const response = await httpClient.get<ApiEnvelope<Raw>>(
    "/quote-budgets/comparison-options",
    { params: { title }, signal },
  );
  return mapComparisonOptions(unwrap(response.data));
}

/** 比较两个核价版本，并请求后端生成 DeepSeek 费用项目差异清单。 */
export async function compareBudgetVersions(
  leftBudgetOrderId: string,
  rightBudgetOrderId: string,
): Promise<QuoteBudgetVersionCompareResult> {
  const response = await httpClient.post<ApiEnvelope<Raw>>(
    "/quote-budgets/compare",
    {
      left_budget_order_id: leftBudgetOrderId,
      right_budget_order_id: rightBudgetOrderId,
    },
    { timeout: 60_000, "axios-retry": { retries: 0 } },
  );
  return mapVersionCompareResult(unwrap(response.data));
}

/** 按交通方式与关键词查询起终点候选。 */
export async function fetchBudgetTransportPoints(
  budgetOrderId: string,
  mode: "air" | "rail",
  q: string,
): Promise<QuoteBudgetTransportPoint[]> {
  const response = await httpClient.get<ApiEnvelope<Raw[]>>(
    `/quote-budgets/${budgetOrderId}/transport-points`,
    { params: { mode, q, limit: 200 } },
  );
  return unwrap(response.data).map(mapTransportPoint);
}

/** 查询指定 SKU 的历史样本 P75 推荐价、降级链与无样本风险码。 */
export async function fetchBudgetPriceCandidates(
  budgetOrderId: string,
  params: {
    resourceType?: string | null;
    skuTableName?: string | null;
    skuId?: string | null;
    serviceMonth?: number | null;
  } = {},
): Promise<QuoteBudgetPriceCandidate> {
  const response = await httpClient.get<ApiEnvelope<Raw>>(
    `/quote-budgets/${budgetOrderId}/price-candidates`,
    {
      params: {
        resource_type: params.resourceType ?? undefined,
        sku_table_name: params.skuTableName ?? undefined,
        sku_id: params.skuId ?? undefined,
        service_month: params.serviceMonth ?? undefined,
      },
    },
  );
  return mapPriceCandidate(unwrap(response.data));
}

/** 保存草稿。 */
export async function saveBudgetDraft(
  budgetOrderId: string,
  request: SaveDraftRequest,
): Promise<QuoteBudgetEditorData> {
  const response = await httpClient.put<ApiEnvelope<Raw>>(
    `/quote-budgets/${budgetOrderId}/draft`,
    mapSaveDraftPayload(request),
    { "axios-retry": { retries: 0 } },
  );
  return mapEditorData(unwrap(response.data));
}

/** 基于来源单复制新版本。 */
export async function copyBudgetVersion(
  sourceBudgetOrderId: string,
  request: CopyVersionRequest,
): Promise<CopyVersionResult> {
  const response = await httpClient.post<ApiEnvelope<Raw>>(
    `/quote-budgets/${sourceBudgetOrderId}/versions`,
    mapCopyVersionPayload(request),
  );
  return mapCopyVersionResult(unwrap(response.data));
}

/** 从其他行程的已定稿核价复制数据，并在当前行程创建新版本。 */
export async function copyBudgetVersionFromHistory(
  targetBudgetOrderId: string,
  sourceBudgetOrderId: string,
  idempotencyKey: string,
): Promise<CopyVersionResult> {
  const response = await httpClient.post<ApiEnvelope<Raw>>(
    `/quote-budgets/${targetBudgetOrderId}/versions/from-history`,
    {
      source_budget_order_id: sourceBudgetOrderId,
      idempotency_key: idempotencyKey,
    },
  );
  return mapCopyVersionResult(unwrap(response.data));
}

/** 继承预算口径，并按当前行程重新生成新版本。 */
export async function createBudgetVersionFromItinerary(
  sourceBudgetOrderId: string,
  request: RebuildVersionRequest,
): Promise<CopyVersionResult> {
  const response = await httpClient.post<ApiEnvelope<Raw>>(
    `/quote-budgets/${sourceBudgetOrderId}/versions/from-itinerary`,
    mapRebuildVersionPayload(request),
  );
  return mapCopyVersionResult(unwrap(response.data));
}

/** 查询按最新行程重建时可由用户确认复用的唯一资源价格。 */
export async function fetchRebuildPriceReusePreview(
  sourceBudgetOrderId: string,
): Promise<PriceReusePreview> {
  const response = await httpClient.get<ApiEnvelope<Raw>>(
    `/quote-budgets/${sourceBudgetOrderId}/versions/from-itinerary/price-reuse-preview`,
  );
  return mapPriceReusePreview(unwrap(response.data));
}

/** 定稿。 */
export async function finalizeBudget(
  budgetOrderId: string,
  request: FinalizeRequest,
): Promise<FinalizeResult> {
  const response = await httpClient.post<ApiEnvelope<Raw>>(
    `/quote-budgets/${budgetOrderId}/finalize`,
    mapFinalizePayload(request),
    { timeout: 60_000, "axios-retry": { retries: 0 } },
  );
  return mapFinalizeResult(unwrap(response.data));
}

/** 取消定稿并恢复草稿编辑态。 */
export async function unfinalizeBudget(budgetOrderId: string): Promise<FinalizeResult> {
  const response = await httpClient.post<ApiEnvelope<Raw>>(
    `/quote-budgets/${budgetOrderId}/unfinalize`,
  );
  return mapFinalizeResult(unwrap(response.data));
}
